import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { WINDOW } from 'src/app/core/core.module';
import { EventObserverService } from 'src/app/core/event-observer.service';
import { UnsubscribeOnDestroy } from 'src/app/shared/utilities/virtual-images/unsubscribe-ondestroy';
import { APP_CONFIG, Settings } from '../config/settings';
import { LocalStorageKeys } from './enums/storage-keys.enum';
import { AuthClientConfig, AuthService, GenericError, User } from '@auth0/auth0-angular';
import { AuthErrors } from '../shared/enums/auth-errors.enum';

export class Identity {
    userId: string;
    contactId: number;
    userName: string;
    firstName: string;
    lastName: string;
    permissionsToken: string;
    organizationId: number;
    email: string;
    defaultPage: string;
    userType: string;

    get fullName(): string {
        return [this.firstName, this.lastName].join(' ');
    }
}

@Injectable({
    providedIn: 'root'
})
export class IdentityService extends UnsubscribeOnDestroy {
    private readonly localStorage$: Observable<StorageEvent>;
    private _authService?: AuthService;
    private storageKey: string;
    private _hasOAuth = false;

    //TODO: set private on FF clean-up
    _isInstantiated = false;
    private readonly _allowedAnonymousPages: string[] = [];
    private readonly _loginPages = ['/signin', '/sso'];

    private readonly _identity$ = new Subject<Identity | null>();
    private readonly _authService$ = new ReplaySubject<AuthService>(1);

    get identity$(): Observable<Identity | null> {
        return this._identity$.asObservable().pipe(
            distinctUntilChanged((p, c) => p?.userId === c?.userId),
            takeUntil(this._cleanup$)
        );
    }

    get authService$(): Observable<AuthService> {
        return this._authService$;
    }
    get loginPages(): string[] {
        return this._loginPages;
    }
    get isAuthenticated$(): Observable<boolean> {
        return this._authService$.pipe(switchMap((authService) => authService.isAuthenticated$));
    }
    get user$(): Observable<User | null | undefined> {
        return this._authService$.pipe(switchMap((authService) => authService.user$));
    }

    get hasOAuth() {
        return this._hasOAuth;
    }

    constructor(
        @Inject(APP_CONFIG) private readonly settings: Settings,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _injector: Injector,
        private readonly _authConfig: AuthClientConfig,
        private readonly _eventObserverService: EventObserverService,
        private readonly _router: Router
    ) {
        super();

        this.storageKey = settings.appName + '.identity';

        this.localStorage$ = this._eventObserverService.observe<StorageEvent>(this._window, 'storage').pipe(
            filter((event) => event.storageArea === localStorage),
            takeUntil(this._cleanup$)
        );

        this.localStorage$
            .pipe(
                filter((event) => event.key === this.storageKey),
                map((event) => event.newValue)
            )
            .subscribe((value) => {
                const parsedUser = value ? (JSON.parse(value) as Identity) : null;
                this.set(parsedUser);
            });
    }

    init() {
        if (this._isInstantiated) {
            return;
        }
        this._isInstantiated = true;

        this._authConfig.set({
            domain: this.settings.authDomain,
            clientId: this.settings.authClientId,
            authorizationParams: {
                audience: `${this._window.location.protocol}${this.settings.coreAPIUrl}`
            },
            useRefreshTokens: true,
            cacheLocation: this.settings.authCacheLocation,
            skipRedirectCallback: window.location.hash?.startsWith('#/signin'),
            httpInterceptor: {
                allowedList: [
                    {
                        uri: `${this.settings.coreAPIUrl}/azureAyaCoreHub*`,
                        allowAnonymous: true
                    },
                    {
                        uri: `${this.settings.coreAPIUrl}*`,
                        tokenOptions: {
                            authorizationParams: {
                                audience: `${this._window.location.protocol}${this.settings.coreAPIUrl}`
                            }
                        },
                        allowAnonymous: false
                    }
                ]
            }
        });
        this._authService = this._injector.get(AuthService);
        this._authService$.next(this._authService);
        this.processErrors();
        this._authService.isAuthenticated$.pipe(takeUntil(this._cleanup$)).subscribe((isAuth: boolean) => {
            this._hasOAuth = isAuth;
        });
    }

    logout() {
        this.delete();
        this._authService?.logout({
            logoutParams: {
                returnTo: this._window.location.origin
            }
        });
        this._router.navigate(['/signin'], {
            queryParams: { loading: true },
            replaceUrl: true
        });
    }

    //Clears any expired auth data. Ex: refresh token.
    //For more info please check: "Clear Expired Refresh token before login"
    clearCachedTokens() {
        this._authService?.logout({
            openUrl: false
        });
    }

    getSignInPage(): string {
        const targetPath = this.getCurrentLocationPath();
        const url = '/signin' + (targetPath.length > 0 ? `?target=${encodeURIComponent(targetPath)}` : '');
        return url;
    }

    getCurrentLocationPath() {
        let path = this._window?.location?.hash?.replace('#', '') ?? this._window?.location?.pathname ?? '';
        if (!path.replaceAll('/', '').replaceAll(' ', '')) {
            path = '';
        }
        return path.split('?')[0];
    }

    private processErrors() {
        if (!this._authService) {
            return;
        }
        this._authService.error$
            .pipe(
                map((e) => e as GenericError),
                map((e: GenericError) => {
                    const pathRoute = this.getCurrentLocationPath().toLowerCase();
                    const isAnonymousPage = this._allowedAnonymousPages
                        .map((p) => p.toLowerCase())
                        .some((p) => pathRoute.startsWith(p));
                    const isLoginPage = this._loginPages
                        .map((p) => p.toLowerCase())
                        .some((p) => pathRoute.startsWith(p));
                    return { error: e?.error, isAnonymousPage, isLoginPage };
                }),
                tap(({ error, isAnonymousPage, isLoginPage }) => {
                    if ((isAnonymousPage || isLoginPage) && error === AuthErrors.InvalidGrant) {
                        this.clearCachedTokens();
                        this._window.location.reload();
                    }
                }),
                filter(
                    ({ error, isAnonymousPage, isLoginPage }) =>
                        (error === AuthErrors.LoginRequired ||
                            error === AuthErrors.InvalidGrant ||
                            error === AuthErrors.MissingRefreshToken) &&
                        !isAnonymousPage &&
                        !isLoginPage
                ),
                switchMap(({ error }) =>
                    this.isAuthenticated$.pipe(
                        map((isAuthenticated) => ({
                            loginRequired: error === AuthErrors.LoginRequired || error === AuthErrors.InvalidGrant,
                            unauthorized: !isAuthenticated
                        }))
                    )
                ),
                filter(({ loginRequired, unauthorized }) => loginRequired || unauthorized),
                take(1)
            )
            .subscribe(() => this.redirectToSignInPage());
    }

    private redirectToSignInPage(): void {
        const url = this.getSignInPage();
        this._router.navigateByUrl(url);
    }

    get current(): Identity {
        //TODO - create local storage service
        const storedIdentity = this._window.localStorage[this.storageKey];
        if (storedIdentity) {
            try {
                const data = JSON.parse(storedIdentity);
                this._identity$.next(Object.assign(new Identity(), data));
                return data;
            } catch (e) {
                console.error(e);
            }
        }
        return null;
    }

    get coreAccessToken$(): Observable<string> {
        return this._authService$.pipe(
            take(1),
            switchMap((authService) => authService.getAccessTokenSilently()),
            distinctUntilChanged()
        );
    }

    delete() {
        this._window.localStorage.removeItem(this.storageKey);
        this._window.localStorage.removeItem(LocalStorageKeys.SILENT_REFRESH_AVAILABLE_DATE);
        this._window.localStorage.removeItem(LocalStorageKeys.SILENT_REFRESH_LOCKED);
        this._window.localStorage.removeItem(LocalStorageKeys.TOKEN_EXPIRES_IN);
        this._window.localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
        this._window.localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
        this._identity$.next(null);
    }

    exists(): boolean {
        return this.hasOAuth && this.current !== null;
    }

    set(data: Identity) {
        if (data && data.userName && data.permissionsToken) {
            //save to local storage
            this._window.localStorage[this.storageKey] = JSON.stringify(data);
            this._identity$.next(Object.assign(new Identity(), data));
        } else {
            this._identity$.next(null);
        }
    }

    hasPermission(moduleName: string, moduleItemName: string): boolean {
        if (!this.exists()) {
            return false;
        }

        const permissionsToken = this.current.permissionsToken.toLowerCase();
        const permissions = permissionsToken.toLowerCase().split(',');
        const permissionName = `${String(moduleName)}:${String(moduleItemName)}`.toLocaleLowerCase();

        const result = permissions.some((p) => p.startsWith(permissionName));
        return result;
    }

    // Used in ticketing permissions. Check if it can be removed.
    authorize(resource, action) {
        let module = '';
        let item = '';
        let isAuthorized = false;

        [module, item] = resource.toLowerCase().split('.');
        const tokenItem = `${module}:${item}`;

        if (this.current) {
            if (item) {
                switch (action) {
                    case 'c': // 'c' the identity.permissionsToken must have '$c'
                        isAuthorized = this.current.permissionsToken.toLowerCase().includes(tokenItem + '$c');
                        break;
                    case 'd': // 'd' the identity.permissionsToken can have '$c' or '$d'
                        isAuthorized =
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$c') ||
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$d');
                        break;
                    case 'u': // 'u' identity.permissionsToken can have '$c', '$d' or '$u'
                        isAuthorized =
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$c') ||
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$d') ||
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$u');
                        break;
                    case 'r': // 'r' identity.permissionsToken can '$c', '$d', '$u' or '$r'
                        isAuthorized = this.current.permissionsToken.toLowerCase().includes(tokenItem + '$');
                        break;
                }
            }
        }
        return isAuthorized;
    }
}
